import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditNoteService } from '../../../api/apiService';
import '../../../App.css';

function CreditNotesCancelled() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]); // [{ original, storno|null }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState({});

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (d) => {
    if (!d) return '';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatMoney = (n) => {
    if (n == null || n === '') return '—';
    const num = typeof n === 'number' ? n : parseFloat(n);
    return isNaN(num) ? String(n) : num.toFixed(2);
  };

  const getClientName = (cn) => {
    if (cn.client_name) return cn.client_name;
    if (Array.isArray(cn.items) && cn.items[0]?.client_name) return cn.items[0].client_name;
    return null;
  };

  const doFetch = useCallback(() => {
    setLoading(true);
    setError(null);
    // Fetch all cancelled CNs (includes originals and storni)
    creditNoteService.getCreditNotes({ limit: 500, status: 'cancelled' })
      .then((res) => {
        const all = (res.data?.creditNotes || []).filter(cn => cn.status === 'cancelled');
        // Separate storni from originals
        const stornoMap = {};
        all.forEach(cn => {
          if (cn.invoice_type === 'storno' && cn.related_credit_note_id) {
            stornoMap[cn.related_credit_note_id] = cn;
          }
        });
        const originals = all.filter(cn => cn.invoice_type !== 'storno');
        const paired = originals.map(cn => ({
          original: cn,
          storno: stornoMap[cn.id] || null,
        }));
        setRows(paired);
      })
      .catch((err) => {
        console.error('Error loading cancelled credit notes:', err);
        setError(err.message || 'Failed to load cancelled credit notes');
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { doFetch(); }, []);

  const handleDownloadPdf = async (cn) => {
    const key = cn.id;
    setDownloading(p => ({ ...p, [key]: true }));
    try {
      const res = await creditNoteService.getCreditNotePdf(cn.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gutschrift_${cn.credit_note_number || cn.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      alert('PDF download failed');
    } finally {
      setDownloading(p => ({ ...p, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1>Cancelled credit notes</h1>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  const thStyle = { padding: '8px 6px', fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '6px 6px', fontSize: 11, verticalAlign: 'top' };

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <h1 style={{ margin: 0 }}>Cancelled credit notes</h1>
          <button
            type="button"
            className="button"
            style={{ fontSize: 12, padding: '4px 12px' }}
            onClick={() => navigate('/admin/credit-notes')}
          >
            ← Back to credit notes
          </button>
        </div>

        {error && (
          <div style={{ padding: 12, marginBottom: 16, background: '#f8d7da', color: '#721c24', borderRadius: 6 }}>
            {error}
          </div>
        )}

        {rows.length === 0 ? (
          <p style={{ color: '#666' }}>No cancelled credit notes.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #dee2e6', background: '#f8f9fa' }}>
                  <th style={thStyle}>Number</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Talent</th>
                  <th style={thStyle}>Client</th>
                  <th style={thStyle}>Net</th>
                  <th style={thStyle}>Gross</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Cancelled</th>
                  <th style={thStyle}>Storno</th>
                  <th style={thStyle}>PDF</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ original: cn, storno }) => (
                  <React.Fragment key={cn.id}>
                    {/* Original cancelled CN */}
                    <tr style={{ borderBottom: storno ? 'none' : '1px solid #eee', color: '#666' }}>
                      <td style={tdStyle}>{cn.credit_note_number || cn.id}</td>
                      <td style={tdStyle}>{cn.invoice_type || '—'}</td>
                      <td style={tdStyle}>{cn.talent_name || '—'}</td>
                      <td style={tdStyle}>{getClientName(cn) || '—'}</td>
                      <td style={tdStyle}>{formatMoney(cn.net_amount)} €</td>
                      <td style={tdStyle}>{formatMoney(cn.gross_amount)} €</td>
                      <td style={tdStyle}>
                        <div>{cn.created_at ? `${formatDate(cn.created_at)} ${formatTime(cn.created_at)}` : '—'}</div>
                        <div style={{ color: '#999' }}>{cn.created_by || ''}</div>
                      </td>
                      <td style={tdStyle}>
                        <div>{cn.cancelled_at ? `${formatDate(cn.cancelled_at)} ${formatTime(cn.cancelled_at)}` : '—'}</div>
                        <div style={{ color: '#999' }}>{cn.cancelled_by || ''}</div>
                      </td>
                      <td style={tdStyle}>
                        {storno
                          ? <span style={{ color: '#dc3545', fontWeight: 600, fontSize: 11 }}>{storno.credit_note_number}</span>
                          : <span style={{ color: '#999', fontSize: 11 }}>—</span>}
                      </td>
                      <td style={tdStyle}>—</td>
                    </tr>

                    {/* Storno row (paired, indented) */}
                    {storno && (
                      <tr style={{ borderBottom: '1px solid #eee', background: '#fff5f5', color: '#dc3545' }}>
                        <td style={{ ...tdStyle, paddingLeft: 18 }}>
                          <span style={{ fontSize: 10, color: '#999', marginRight: 4 }}>↳</span>
                          {storno.credit_note_number || storno.id}
                        </td>
                        <td style={tdStyle}>Stornogutschrift</td>
                        <td style={tdStyle}>{storno.talent_name || '—'}</td>
                        <td style={tdStyle}>{getClientName(storno) || '—'}</td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{formatMoney(storno.net_amount)} €</td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{formatMoney(storno.gross_amount)} €</td>
                        <td style={tdStyle}>
                          <div style={{ color: '#999' }}>{storno.created_by || ''}</div>
                        </td>
                        <td style={tdStyle}>—</td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 10, color: '#999' }}>Reversal of {cn.credit_note_number}</span>
                        </td>
                        <td style={tdStyle}>
                          <button
                            type="button"
                            className="button talent-cn-export-btn"
                            style={{ fontSize: 11, padding: '2px 8px' }}
                            disabled={!!downloading[storno.id]}
                            onClick={() => handleDownloadPdf(storno)}
                          >
                            {downloading[storno.id] ? '…' : 'PDF'}
                          </button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreditNotesCancelled;
